/**
 * useProductHeroImage – resolve a product's primary display image from media_assets.
 *
 * Priority:
 *   1. kind='cover' asset
 *   2. first kind='gallery' asset
 *   3. null (caller handles fallback)
 *
 * Variant priority within each asset: path_1280 → path_640 → path_320
 *
 * This is the single source of truth for product hero imagery.
 * Used by product pages, cards, and Share Studio.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset, VariantWidth } from '@/lib/media/mediaTypes';

export function useProductHeroImage(
  productId: string | null | undefined,
  variant: VariantWidth = 640,
) {
  return useQuery<string | null>({
    queryKey: ['product-hero-image', productId, variant],
    queryFn: async () => {
      if (!productId) return null;

      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .eq('owner_type', 'product')
        .eq('owner_id', productId)
        .in('kind', ['cover', 'gallery'])
        .is('season_key', null)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
        .limit(10);

      if (error) throw error;
      if (!data || data.length === 0) return null;

      // Prefer cover over gallery
      const cover = data.find((a) => a.kind === 'cover');
      const best = cover ?? data[0];
      const asset = best as unknown as MediaAsset;

      return resolveAssetUrl(asset, variant) ?? null;
    },
    enabled: !!productId,
    staleTime: 60_000,
  });
}
